import { getMegaModeInfo, MegaModeBanner } from '@commander/auth';
import { FEATURE_FLAGS, useFeatureFlag } from '@commander/feature-flags';
import type React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { GlobalHeader } from '../components/GlobalHeader';

interface ShellLayoutProps {
  children?: React.ReactNode;
}

export function ShellLayout({ children }: ShellLayoutProps) {
  const isMegaMode = !!getMegaModeInfo();
  const location = useLocation();
  const isFlagOn = useFeatureFlag(FEATURE_FLAGS.ACTION_DASH);

  // Dev builds only, so a production user cannot switch the feed on past the flag.
  const isLocalOverride =
    import.meta.env.DEV &&
    typeof window !== 'undefined' &&
    (new URLSearchParams(window.location.search).get('action_dash') === 'true' ||
      new URLSearchParams(window.location.search).get('action_dash') === '1' ||
      localStorage.getItem('action_dash') === 'true');

  const isActionDashEnabled = isFlagOn || isLocalOverride;
  const isActionDashRoute =
    location.pathname === '/dashboard' ||
    location.pathname === '/' ||
    location.pathname === '/action-dash';

  if (isActionDashRoute && isActionDashEnabled) {
    return (
      <div className="min-h-screen bg-background">
        {isMegaMode && (
          <div className="fixed top-0 inset-x-0 z-[60]">
            <MegaModeBanner />
          </div>
        )}
        <div className={isMegaMode ? 'pt-[44px]' : ''}>{children || <Outlet />}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Read-only megamode banner — fixed over the shell's fixed header */}
      <div className="fixed top-0 inset-x-0 z-[60]">
        <MegaModeBanner />
      </div>

      {/* Global Header - Fixed at top, pushed below the banner in MegaMode */}
      <GlobalHeader offsetTop={isMegaMode} />

      {/* Main Content - Top padding clears the fixed header (+ banner in MegaMode) */}
      <main className={isMegaMode ? 'pt-[100px]' : 'pt-16'}>
        <div className="container mx-auto px-4 md:px-6 lg:px-[54px] py-8">
          {children || <Outlet />}
        </div>
      </main>
    </div>
  );
}
